'use client'

import * as React from 'react'

import NextLink from '@/components/client/seo/link'
import { useTranslation } from '@/hooks/client'
import SvgBars from '@/icons/regular/Bars'
import SvgMagnifyingGlass02 from '@/icons/regular/MagnifyingGlass02'
import SvgXmark from '@/icons/regular/Xmark'
import SvgLogo from '@/images/logo-dark'
import SvgLogoWhite from '@/images/logo-white'
import { tw } from '@/lib/utils'
import { localizedHref } from '@/lib/utils/localized-href'

import { useNavContext } from './nav-context-provider'

// The bar that contains the logo, menu links, hamburger. Positioning and the
// scroll show/hide transform live on the parent <nav> (NavWrapper); this is
// just the in-flow visible bar.
export const NavTopbar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navContext = useNavContext()
  const isHomepage = navContext.pageType === 'home'

  // backgroundColor is normally managed via direct DOM manipulation in the
  // scroll handler. Light themes switch to white for an open mobile drawer;
  // dark routes begin transparent over their hero and become navy on scroll.
  const forceWhiteBg =
    !navContext.isDarkNav && (navContext.isMobileMenuVisible || (navContext.isMobile && navContext.current.containerId))
  const forceDarkBg =
    navContext.isDarkNav && navContext.isMobile && (navContext.isMobileMenuVisible || !!navContext.current.containerId)
  const backgroundStyle = forceDarkBg
    ? { backgroundColor: '#142546' }
    : forceWhiteBg
      ? { backgroundColor: 'white' }
      : undefined
  return (
    <section
      ref={navContext.registerNavRef}
      data-menu-clickaway
      className={tw(
        // In flow (relative) within the positioned <nav>, so the bar reserves
        // its height and the in-flow disclaimer above pushes it + the page down.
        'relative z-[1000] min-h-20 w-full',
        navContext.isDarkNav && 'bg-transparent',
        navContext.isDarkNav &&
          'after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-px after:h-px after:bg-white/10',
        // The light bar is flat (no shadow); the scroll handler paints the
        // same cream inline, and cream stays under the open desktop band.
        !navContext.isDarkNav && 'bg-cream',
        isHomepage && 'storyblok-nav--transparent'
      )}
      style={backgroundStyle}
    >
      <nav
        className={tw(
          'topbar-wrapper laptop:w-full mx-auto flex w-[1440px] max-w-full items-center justify-center pt-0 pb-0',
          navContext.pageType === 'home' && 'home-page px-4 md:px-0'
        )}
      >
        <div
          className={tw(
            'topbar flex w-full items-center justify-between gap-0 py-4 pr-3 pl-6 md:gap-4 md:px-12 nav:py-0',
            navContext.pageType === 'home' && 'home-page px-0'
          )}
        >
          {children}
        </div>
      </nav>
    </section>
  )
}

interface SearchProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isNavTextWhite?: boolean
  loading?: boolean
}

export const Search: React.FC<SearchProps> = ({ loading, className, ...props }) => {
  const navContext = useNavContext()
  const { t } = useTranslation()
  return (
    <button
      {...props}
      type="button"
      aria-label={t('nav.search', 'Search')}
      className={tw(
        className,
        'search border-navy/20 hover:border-navy/60 relative flex h-[42px] w-12 items-center justify-center rounded-full border-[0.857px] bg-transparent md:h-12',
        navContext.isMenuTextWhite && 'search--white border-white/20 hover:border-white/60',
        navContext.isDarkNav && 'hover:bg-white/[0.08]',
        loading && 'cursor-wait'
      )}
    >
      <SvgMagnifyingGlass02
        role="img"
        className={tw(
          'absolute z-4 flex min-h-5 min-w-5 brightness-95 contrast-75 hue-rotate-198 invert-10 saturate-1731 sepia-57',
          navContext.isMenuTextWhite && 'brightness-104 contrast-106 hue-rotate-78 invert-95 saturate-0 sepia',
          // The white-text chain ends in `sepia`, which tints the icon cream.
          // Dark-nav routes want pure white: crush to black, invert, no sepia.
          navContext.isDarkNav && 'brightness-0 invert sepia-0'
        )}
        aria-hidden
      />
    </button>
  )
}

type CloseProps = React.ButtonHTMLAttributes<HTMLButtonElement>

export const Close: React.FC<CloseProps> = ({ className, ...props }) => {
  const navContext = useNavContext()
  const { t } = useTranslation()
  return (
    <button
      {...props}
      type="button"
      aria-label={t('nav.closeMenu', 'Close menu')}
      className={tw(
        'search search--close relative flex h-[42px] w-12 items-center justify-center rounded-full border-none bg-transparent pr-[0.063rem] hover:border-none md:h-12',
        navContext.isDarkNav && 'hover:bg-white/[0.08]',
        className
      )}
    >
      <SvgXmark
        role="img"
        className={tw(
          'absolute z-4 flex min-h-5 min-w-5 bg-center brightness-95 contrast-75 hue-rotate-198 invert-10 saturate-1731 sepia-57',
          navContext.isMenuTextWhite && 'brightness-104 contrast-106 hue-rotate-78 invert-95 saturate-0 sepia',
          navContext.isDarkNav && 'brightness-0 invert sepia-0'
        )}
        aria-hidden
      />
    </button>
  )
}

export const Toggler: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className, ...props }) => {
  const navContext = useNavContext()
  const { t } = useTranslation()
  return (
    <button
      type="button"
      aria-label={t('nav.toggleNavigationAria', 'toggle navigation')}
      className={tw(
        'hamburger relative flex h-[42px] w-12 items-center justify-center rounded-full border-none bg-transparent md:h-12',
        '[&:hover]:bg-[#3b4e6b] [&:hover_i]:brightness-105 [&:hover_i]:contrast-[102]',
        '[&:hover_i]:hue-rotate-285 [&:hover_i]:invert [&:hover_i]:saturate-0 [&:hover_i]:sepia',
        navContext.isMenuTextWhite && 'hamburger--white',
        className
      )}
      {...props}
    >
      <SvgBars
        role="img"
        className={tw(
          'absolute z-4 flex min-h-5 min-w-5 bg-center brightness-95 contrast-75 hue-rotate-198 invert-10 saturate-1731 sepia-57',
          navContext.isMenuTextWhite && 'brightness-104 contrast-106 hue-rotate-78 invert-95 saturate-0 sepia',
          navContext.isDarkNav && 'brightness-0 invert sepia-0'
        )}
        aria-hidden
      />
    </button>
  )
}

export const Logo: React.FC = () => {
  const { t, locale } = useTranslation()
  const navContext = useNavContext()

  const Logo = navContext.isMenuTextWhite ? SvgLogoWhite : SvgLogo
  return (
    <NextLink
      prefetch={false}
      // Not a bare "/" — on a German page that links back into the English tree.
      href={localizedHref(locale, '/')}
      className={tw(
        'mr-0 flex h-7 min-h-12 min-w-52 items-center',
        'mr-5 w-full max-w-52 min-w-0 md:mr-0 md:w-auto md:max-w-none md:min-w-52'
      )}
      aria-label={t('nav.logoAria', 'HearingTracker Logo')}
    >
      <Logo className="h-7" width="213" height="28" aria-hidden focusable={false} />
    </NextLink>
  )
}
